import React, { useState } from "react";
import "../styles/Page.css";
import { useQuery, useMutation, gql } from "@apollo/client";

// GraphQL Definitions
const GET_SELLERS = gql`
  query {
    getSellers {
      id
      name
      company
      email
      phone
      status
    }
  }
`;

const CHANGE_SELLER_STATUS = gql`
  mutation ChangeSellerStatus($id: String!, $status: String!) {
    changeSellerStatus(id: $id, status: $status) {
      id
      name
      status
    }
  }
`;

function Users() {
  const [message, setMessage] = useState("");

  const { loading, error, data, refetch } = useQuery(GET_SELLERS, {
    fetchPolicy: 'network-only',
  });

  const [changeSellerStatus] = useMutation(CHANGE_SELLER_STATUS);

  const sellers = data?.getSellers || [];

  // Approve or Reject Individual Seller
  const handleStatusChange = async (id, status) => {
    try {
      const { data } = await changeSellerStatus({
        variables: { id, status },
      });

      if (data?.changeSellerStatus) {
        const updated = data.changeSellerStatus;
        setMessage(`✅ Seller ${updated.name} marked as ${updated.status}.`);
        // We could manually update cache here, or rely on refetch/automatic updates if ID matches
        // For simplicity and correctness with small lists, refetch is fine, but automatic update happens if id is present.
      }
    } catch (err) {
      console.error("Error updating status:", err);
      setMessage("❌ Update failed: " + err.message);
    }
  };

  // Approve All Pending Sellers
  const handleApproveAll = async () => {
    const pendingSellers = sellers.filter((s) => s.status === "pending");
    if (pendingSellers.length === 0) {
      alert("No pending sellers to approve.");
      return;
    }

    try {
      // Sequentially approve
      // Ideally backend should have bulk approve, but keeping logic same as before
      for (const seller of pendingSellers) {
        await handleStatusChange(seller.id, "approved");
      }
      alert("✅ All pending sellers approved!");
      refetch();
    } catch (err) {
      alert("❌ Error approving some sellers");
    }
  };

  if (loading) return <p>Loading sellers...</p>;
  if (error) return <p className="error-text">Error loading sellers: {error.message}</p>;

  return (
    <div className="page">
      <h2>👤 Seller Management</h2>

      <div className="bulk-actions">
        <button className="approve-all-btn" onClick={handleApproveAll}>
          ✅ Approve All Pending Sellers
        </button>
      </div>

      {message && <p className="status-message">{message}</p>}

      <table className="seller-table">
        <thead>
          <tr>
            <th>Name</th>
            <th>Company</th>
            <th>Email</th>
            <th>Phone</th>
            <th>Status</th>
            <th>Actions</th>
          </tr>
        </thead>

        <tbody>
          {sellers.length === 0 ? (
            <tr>
              <td colSpan="6">No sellers found.</td>
            </tr>
          ) : (
            sellers.map((s) => (
              <tr key={s.id}>
                <td>{s.name}</td>
                <td>{s.company}</td>
                <td>{s.email}</td>
                <td>{s.phone}</td>
                <td>
                  <span
                    className={`status-badge ${s.status === "approved"
                      ? "status-approved"
                      : s.status === "rejected"
                        ? "status-rejected"
                        : "status-pending"
                      }`}
                  >
                    {s.status}
                  </span>
                </td>
                <td>
                  <div className="actions">
                    <button
                      className="approve-btn"
                      onClick={() => handleStatusChange(s.id, "approved")}
                    >
                      ✅ Approve
                    </button>
                    <button
                      className="reject-btn"
                      onClick={() => handleStatusChange(s.id, "rejected")}
                    >
                      ❌ Reject
                    </button>
                  </div>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

export default Users;
